/**
 * /de/entscheidungsprotokoll — decision-log concept page (German). Wedge.
 * Links to /de/prd-anforderungsdokument.
 */
import { MarketingShell } from './MarketingShell';

export function DeEntscheidungsprotokoll() {
  return (
    <MarketingShell
      currentLocale="de"
      hreflangPath="/entscheidungsprotokoll"
      hreflangLocales={['de']}
      documentTitle="Decision Log (Entscheidungsprotokoll): warum & wie · Draftlize"
      documentDescription="Ein Decision Log bewahrt das „Warum“ jeder Produktentscheidung. Die meisten verrotten: einmal geschrieben, nie gelesen. Wie du ein lebendiges baust, mit Draftlize."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Leitfaden · Decision Log</span>
          <h1 className="serif" style={{ fontSize: 'clamp(31px, 4.2vw, 52px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            Das Entscheidungsprotokoll, das<br />
            <em>„Warum haben wir das gemacht?“ beantwortet.</em>
          </h1>
          <p className="lede">
            Ein Entscheidungsprotokoll (Decision Log) ist das Register, das zu jeder Produktentscheidung festhält, <em>warum</em> so entschieden wurde. Monate später, wenn jemand dieselbe Debatte neu aufmacht, klärt sie sich in einer Minute – sofern Kontext, Optionen und Trade-offs griffbereit sind. Das Problem: Die meisten Protokolle <em>verrotten</em> – einmal geschrieben, nie gelesen, still veraltet. Hier: wie du es führst und lebendig hältst.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Was ins Entscheidungsprotokoll gehört.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Die Entscheidung</strong> – was entschieden wurde, in einem Satz.</li>
            <li><strong>Der Kontext</strong> – warum jetzt entschieden werden musste.</li>
            <li><strong>Optionen & Trade-offs</strong> – was abgewogen und warum verworfen wurde. <em>Der später nützlichste Teil</em>.</li>
            <li><strong>Owner & Datum</strong> – wer und wann.</li>
            <li><strong>Auswirkung</strong> – welche Specs oder Entscheidungen betroffen sind.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Dieses Format deckt sich mit dem ADR (Architecture Decision Record). Viele Entscheidungen entstehen in Meetings und fließen dann ins <a href="/de/prd-anforderungsdokument" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>PRD</a> – das Protokoll bewahrt das „Warum“ dahinter.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Warum Protokolle verrotten. <em>Und wie du es verhinderst.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Ein Entscheidungsprotokoll als flacher Text driftet ab dem Moment des Schreibens von der Realität weg. Eine spätere Entscheidung kippt eine frühere, das Dokument bleibt auf der alten. In Draftlize sind Entscheidungen „lebendige Karten“.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Jede Entscheidung ist eine adressierbare Karte</h3>
            <p>Eine Entscheidung = eine Karte, mit Kontext, Optionen, Begründung und Owner, per ID abrufbar. Statt in einem Dokument zu versinken, bleibt sie zeigbar.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Die gekippte Entscheidung wird „stale“</h3>
            <p>Kippt eine neue Entscheidung eine alte, werden die verknüpfte alte – und die Specs darauf – automatisch „stale“. Geklärtes wird nicht neu verhandelt.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Dein KI-Agent liest und schreibt es</h3>
            <p>Das Protokoll wird Kontext, den Claude Code oder Cursor via MCP lesen. Der Agent schreibt den nächsten Schritt und weiß, „warum“ so entschieden wurde.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Die meisten Entscheidungsprotokolle liest schon am nächsten Tag niemand mehr. Und so beginnt sechs Monate später dieselbe Debatte bei null.
        </blockquote>
        <cite>Bewahre jede Entscheidung mit ihrem Kontext und melde, was sie betrifft, wenn sie sich ändert. Das ist Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Häufige Fragen.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Entscheidungsprotokoll oder Meeting-Protokoll?', a: 'Das Meeting-Protokoll ist „die Mitschrift des Meetings“; das Entscheidungsprotokoll ist „das Register der Entscheidungen“. Nur die Entscheidungen aus dem Protokoll zu ziehen und zu sammeln – das bringt später den Nutzen.' },
            { q: 'Wer sollte es pflegen?', a: 'In der Regel die für die Entscheidung verantwortliche Person (PM oder Lead). Wichtig ist, das „Warum“ jeder Entscheidung nachvollziehbar zu halten.' },
            { q: 'Wie detailliert?', a: 'Es reicht, wenn Entscheidung, Kontext und Trade-offs klar sind. Mehr als die Länge zählt, zu erkennen, wann eine Entscheidung veraltet.' },
            { q: 'Ist das dasselbe wie ein ADR?', a: 'Das ADR ist eine Art technisches Entscheidungsprotokoll. Draftlize behandelt ADRs und Produktentscheidungen als dieselbe „lebendige Karte“.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Ein Entscheidungsprotokoll, das nicht verrottet <em>– gratis mit $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>$5 Gratis-Guthaben für neue Accounts</span>
          <span>Zahle nur, was du nutzt</span>
          <span>Guthaben verfällt nie</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Mach jede Entscheidung zur Karte mit Kontext; wird sie gekippt, wird sie von selbst „stale“, und dein KI-Agent liest sie. Ein lebendiges Entscheidungsprotokoll, mit Draftlize.
          </p>
          <a className="btn-primary" href="/">Gratis mit $5 starten</a>
        </div>
      </section>
    </MarketingShell>
  );
}
